import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { HorizontalAlignmentEnum, VerticalAlignmentEnum } from '@repo/ui/src/enums/shared-enums';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ImageProps, ToutContentProps } from '@repo/ui/src/types';
import { imageFullWidthXs } from '@repo/ui/src/styles/variables';
import { classNames } from '@repo/ui/src/utils/classNames';
import Image from '@repo/ui/src/components/Image/New';

export interface FullWidthCardProps {
  toutContent: ToutContentProps;
  image: ImageProps;
  horizontalAlignment?: HorizontalAlignmentEnum;
  verticalAlignment?: VerticalAlignmentEnum;
  backgroundImageUrl: string;
  buttonNegative?: boolean;
  isVideo?: any;
  _testID?: string;
  index?: number;
  onClick?: () => void;
  isInGrid?: boolean;
}
const fullVerticalAlignmentClasses = {
  [VerticalAlignmentEnum.Top]: 'items-start',
  [VerticalAlignmentEnum.Center]: 'items-center',
  [VerticalAlignmentEnum.Bottom]: 'items-end',
};
const fullHorizontalAlignmentClasses = {
  [HorizontalAlignmentEnum.Left]: 'justify-start',
  [HorizontalAlignmentEnum.Center]: 'justify-center',
  // padding should only be on the right side
  [HorizontalAlignmentEnum.Right]: 'justify-end md:pr-40',
};

const FullWidthCard = ({
  toutContent,
  image,
  horizontalAlignment,
  verticalAlignment,
  onClick,
  backgroundImageUrl,
  buttonNegative = false,
  isVideo,
  _testID,
  index,
  isInGrid = false,
}: FullWidthCardProps) => {
  const imageUrl = buildImageSrc(image, imageFullWidthXs, 1, 1);

  return (
    <div
      className={classNames(
        'relative grid bg-cover bg-no-repeat bg-center',
        !isVideo && 'bg-black',
        toutContent?.youtubeLink && 'cursor-pointer',
      )}
      onClick={onClick}
      style={{
        backgroundImage: backgroundImageUrl ? `url("${backgroundImageUrl}")` : 'none',
      }}
      data-test-id={`full_width_card_${_testID}${index}`}
    >
      <div
        className={classNames(
          'flex flex-col relative',
          horizontalAlignment === HorizontalAlignmentEnum.Left
            ? 'sm:flex-row-reverse'
            : 'sm:flex-row',
          horizontalAlignment === HorizontalAlignmentEnum.Left && 'bg-gradient-to-l',
          horizontalAlignment === HorizontalAlignmentEnum.Right && 'bg-gradient-to-r',
          horizontalAlignment === HorizontalAlignmentEnum.Center
            ? 'bg-[rgba(0,0,0,0.6)]'
            : 'from-transparent via-50% via-[rgba(0,0,0,0.6)] to-[rgba(0,0,0,0.6)]',
        )}
      >
        {image?.image && horizontalAlignment !== HorizontalAlignmentEnum.Center && (
          <div className="flex-1 h-auto justify-center items-center hidden sm:flex">
            {image?.image && (
              <Image
                src={imageUrl}
                alt={image.image.name!}
                className="pt-6 sm:pt-0 w-[70%] sm:w-1/2"
              />
            )}
          </div>
        )}
        <div></div>
        <div
          className={classNames(
            'flex flex-1 h-auto',
            !isInGrid && 'py-12',
            horizontalAlignment
              ? fullHorizontalAlignmentClasses[horizontalAlignment]
              : fullHorizontalAlignmentClasses[HorizontalAlignmentEnum.Center],
            verticalAlignment
              ? fullVerticalAlignmentClasses[verticalAlignment]
              : fullVerticalAlignmentClasses[VerticalAlignmentEnum.Center],
          )}
        >
          <ToutContent
            dark
            headingStyleVariant="h2"
            divider={!!toutContent.description}
            {...toutContent}
            buttonNegative={buttonNegative}
            className="sm:max-w-[75%] md:max-w-[50%] p-6 z-10 sm:[&_p]:line-clamp-4 [&_button]:mt-4 sm:p-gutterLG lg:max-w-[80%]"
          />
        </div>
      </div>
    </div>
  );
};

export default FullWidthCard;
